import { useTranslations } from '@vocab/react';
import { Box, Column, Columns, Dropdown, Text } from 'braid-design-system';
import type React from 'react';
import type { FC } from 'react';

import translations from '@seek/rr-caja/src/.vocab';

import type { AvailableAnswer } from '../../sdk';

export enum AnswerRangeType {
  Minimum = 0,
  Maximum = 1,
}

type AnswerSelectHandler = (answerId: string, type: AnswerRangeType) => void;

interface Props {
  questionId: string;
  answers: AvailableAnswer[];
  onChange: AnswerSelectHandler;
}

const getSelectedAnswers = (
  orderedAnswers: AvailableAnswer[],
): [AvailableAnswer?, AvailableAnswer?] => {
  const preferredAnswers = orderedAnswers.filter((oa) => oa.preferred);

  const minimumSelectedAnswer =
    preferredAnswers.length > 0 ? preferredAnswers[0] : undefined;

  const maximumSelectedAnswer =
    preferredAnswers.length > 1
      ? orderedAnswers.filter((a) => a.preferred).slice(-1)[0]
      : undefined;

  return [minimumSelectedAnswer, maximumSelectedAnswer];
};

export const AnswerRange: FC<Props> = ({ questionId, answers, onChange }) => {
  const { t } = useTranslations(translations);

  const orderedAnswers = [...answers].sort((a, b) => a.order - b.order);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [minimumSelectedAnswer, maximumSelectedAnswer] =
    getSelectedAnswers(orderedAnswers);

  const minimumAvailableAnswers = orderedAnswers.slice(0, -1);
  const maximumAvailableAnswers = minimumSelectedAnswer
    ? orderedAnswers
        .slice(1)
        .filter((a) => a.order > minimumSelectedAnswer.order)
    : orderedAnswers.slice(1);

  const onChangeHandler =
    (type: AnswerRangeType) => (event: React.FormEvent<HTMLSelectElement>) => {
      onChange(event.currentTarget.value, type);
    };

  return (
    <Box key={`AnswerRange-${questionId}`}>
      <Columns space="medium" alignY="center" collapseBelow="tablet">
        <Column>
          <Dropdown
            id={`MinimumId-${questionId}`}
            placeholder={t('question.placeholder.rangeMinimum')}
            aria-label={t('question.placeholder.rangeMinimum')}
            value={minimumSelectedAnswer?.id || ''}
            onChange={onChangeHandler(AnswerRangeType.Minimum)}
            reserveMessageSpace={false}
          >
            {minimumAvailableAnswers.map((answer) => (
              <option key={answer.id} value={answer.id}>
                {answer.text}
              </option>
            ))}
          </Dropdown>
        </Column>
        <Column width="content">
          <Text>{t('question.to')}</Text>
        </Column>
        <Column>
          <Dropdown
            id={`MaximumId-${questionId}`}
            placeholder={t('question.placeholder.rangeMaximum')}
            aria-label={t('question.placeholder.rangeMaximum')}
            value={maximumSelectedAnswer?.id || ''}
            onChange={onChangeHandler(AnswerRangeType.Maximum)}
            reserveMessageSpace={false}
            disabled={!Boolean(minimumSelectedAnswer)}
          >
            {maximumAvailableAnswers.map((answer) => (
              <option key={answer.id} value={answer.id}>
                {answer.text}
              </option>
            ))}
          </Dropdown>
        </Column>
      </Columns>
    </Box>
  );
};
